// src/pages/marketing/UKImmigrationHub.tsx
// src/pages/marketing/UKImmigrationHub.tsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight, CheckCircle2, Flag, Rocket, Briefcase, GraduationCap,
  Heart, Stethoscope, Users, Award, AlertTriangle, TrendingUp,
  Calendar, FileText, DollarSign, Calculator, Languages,
  Home, Plane, Zap, Sparkles, Crown, Shield, Brain, Cpu, Layers,
  Command, Eye, Mic, FileCode, ExternalLink, Star as StarIcon,
  Globe, Network, MessageSquare, Send, Target, Activity, Wifi, Radio,
  Terminal, GitBranch, Plus, Clock, Lock, Compass, Map, Building2,
  MessageCircle, Wallet, BarChart, UserCheck, UserPlus, Crown as CrownIcon,
  Atom, Infinity, Fingerprint, Scale, ClipboardCheck, Download,
  Circle, Link as LinkIcon, CheckCircle, AlertCircle, BarChart3
} from 'lucide-react';

// ============================
// PAGE EFFECTS - CSS ANIMATIONS
// ============================
const animationStyles = `
@keyframes twinkle { 0%, 100% { opacity: 0.1; transform: scale(1); } 50% { opacity: 0.8; transform: scale(1.2); } }
@keyframes pulse-slow { 0%, 100% { opacity: 0.2; transform: scale(1); } 50% { opacity: 0.6; transform: scale(1.05); } }
@keyframes float { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-10px); } }
@keyframes glow-pulse { 0%, 100% { box-shadow: 0 0 5px rgba(59,130,246,0.3); } 50% { box-shadow: 0 0 25px rgba(59,130,246,0.7); } }
@keyframes quantum-spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
@keyframes blink { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.7; transform: scale(1.05); } }
@keyframes border-pulse { 0%, 100% { border-color: rgba(139,92,246,0.3); } 50% { border-color: rgba(236,72,153,0.6); } }
@keyframes spin-slow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
@keyframes ping { 0% { transform: scale(0.2); opacity: 0.8; } 80% { transform: scale(1.2); opacity: 0; } 100% { transform: scale(1); opacity: 0; } }
@keyframes pulse-ring { 0% { box-shadow: 0 0 0 0 rgba(59,130,246,0.4); } 70% { box-shadow: 0 0 0 10px rgba(59,130,246,0); } 100% { box-shadow: 0 0 0 0 rgba(59,130,246,0); } }

.animate-twinkle { animation: twinkle 3s ease-in-out infinite; }
.animate-pulse-slow { animation: pulse-slow 4s ease-in-out infinite; }
.animate-float { animation: float 6s ease-in-out infinite; }
.animate-glow { animation: glow-pulse 3s ease-in-out infinite; }
.animate-quantum-spin { animation: quantum-spin 8s linear infinite; }
.animate-blink { animation: blink 1.5s ease-in-out infinite; }
.animate-border-pulse { animation: border-pulse 2s ease-in-out infinite; }
.animate-spin-slow { animation: spin-slow 20s linear infinite; }
.animate-ping { animation: ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite; }
.pulse-ring { animation: pulse-ring 2s ease-out infinite; }

@media (prefers-reduced-motion: reduce) {
  .animate-twinkle, .animate-pulse-slow, .animate-float, .animate-glow, .animate-blink, .animate-ping {
    animation: none !important;
  }
}
`;

if (typeof document !== 'undefined' && !document.getElementById('uk-hub-animation-styles')) { 
  const style = document.createElement('style'); 
  style.id = 'uk-hub-animation-styles';
  style.textContent = animationStyles; 
  document.head.appendChild(style); 
}

// ============================
// ANIMATED BACKGROUND
// ============================
const AnimatedBackground = () => (
  <div className="fixed inset-0 overflow-hidden pointer-events-none -z-10">
    <div className="absolute inset-0 bg-gradient-to-br from-[#020617] via-[#0a0f2a] to-[#020617]"></div>
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.2),transparent_50%)]"></div>
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(168,85,247,0.15),transparent_50%)]"></div>
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_40%_50%,rgba(6,182,212,0.1),transparent_50%)]"></div>
    
    {[...Array(80)].map((_, i) => (
      <div 
        key={i} 
        className="absolute bg-white rounded-full animate-twinkle" 
        style={{
          left: `${Math.random() * 100}%`, 
          top: `${Math.random() * 100}%`,
          width: `${Math.random() * 3 + 1}px`, 
          height: `${Math.random() * 3 + 1}px`,
          animationDelay: `${Math.random() * 5}s`, 
          opacity: 0.1 + Math.random() * 0.5,
          animationDuration: `${2 + Math.random() * 4}s`
        }} 
      />
    ))}
    
    <div className="absolute inset-0 opacity-[0.03]">
      <svg className="absolute w-full h-full">
        <defs>
          <pattern id="grid" patternUnits="userSpaceOnUse" width="40" height="40">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(59,130,246,0.3)" strokeWidth="0.5"/>
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid)" />
      </svg>
    </div>
  </div>
);

// ============================
// PERSONALIZED INTELLIGENCE PROFILE CARD
// ============================
const PersonalizedIntelligenceProfile = () => {
  const [emotionalState] = useState('OPTIMISTIC');
  const [urgencyScore] = useState(0.78);
  const [complexityProfile] = useState('HIGH');
  const [learningMode] = useState('ADAPTIVE');

  return (
    <div className="border-0 shadow-xl bg-white/10 backdrop-blur-sm rounded-2xl">
      <div className="p-6">
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-purple-400 animate-pulse" />
            <h3 className="text-lg font-semibold text-white">Personalized Intelligence Profile</h3>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="text-sm text-white/60">Emotional State</div>
              <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium animate-pulse ${
                emotionalState === 'OPTIMISTIC' ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white border-0' :
                emotionalState === 'CONFIDENT' ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white border-0' :
                'bg-gradient-to-r from-blue-500 to-cyan-600 text-white border-0'
              }`}>
                {emotionalState}
              </span>
            </div>
            
            <div className="space-y-2">
              <div className="text-sm text-white/60">Urgency Level</div>
              <div className="w-full bg-white/10 rounded-full h-2">
                <div 
                  className="bg-gradient-to-r from-green-400 via-yellow-400 to-red-400 h-2 rounded-full transition-all duration-1000 animate-pulse"
                  style={{ width: `${urgencyScore * 100}%` }}
                ></div>
              </div>
            </div>

            <div className="space-y-2">
              <div className="text-sm text-white/60">Complexity Profile</div>
              <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-gradient-to-r from-indigo-500 to-purple-600 text-white border-0 animate-pulse">
                {complexityProfile}
              </span>
            </div>

            <div className="space-y-2">
              <div className="text-sm text-white/60">Learning Mode</div>
              <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-gradient-to-r from-cyan-500 to-blue-600 text-white border-0 animate-pulse">
                {learningMode}
              </span>
            </div>
          </div>

          {/* System Status Indicator */}
          <div className="mt-4 pt-3 border-t border-white/10">
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                <span className="text-white/60">Quantum Link Active</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></div>
                <span className="text-white/60">Real-time Sync</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-purple-500 animate-pulse"></div>
                <span className="text-white/60">AI Processing</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================
// TOOL CONNECTION MATRIX WITH BLINKING LIGHTS
// ============================
const UKToolsConnectionMatrix = () => {
  // `path` is each tool's real registered route (verified against
  // UKToolsEcosystem.tsx) -- this used to be derived as
  // `/${id}-assessment`, which only happened to match 3 of these 12 real
  // routes (e.g. '/student-visa-assessment' isn't real; the route is
  // '/student-assessment') and sent the other 9 to dead pages.
  const ukTools = [
    { id: 'skilled-worker', name: 'Skilled Worker', path: '/skilled-worker-assessment', icon: <Briefcase className="h-4 w-4" />, status: 'active', powerLevel: 92, category: 'Work Visa' },
    { id: 'student-visa', name: 'Student Visa', path: '/student-assessment', icon: <GraduationCap className="h-4 w-4" />, status: 'active', powerLevel: 88, category: 'Study' },
    { id: 'global-talent', name: 'Global Talent', path: '/global-talent-assessment', icon: <Award className="h-4 w-4" />, status: 'active', powerLevel: 85, category: 'Talent' },
    { id: 'family-visa', name: 'Family Visa', path: '/family-assessment', icon: <Heart className="h-4 w-4" />, status: 'active', powerLevel: 90, category: 'Family' },
    { id: 'health-care', name: 'Health Care', path: '/health-care-assessment', icon: <Stethoscope className="h-4 w-4" />, status: 'active', powerLevel: 87, category: 'Healthcare' },
    { id: 'youth-mobility', name: 'Youth Mobility', path: '/youth-mobility-assessment', icon: <Users className="h-4 w-4" />, status: 'active', powerLevel: 82, category: 'Youth' },
    { id: 'document-intel', name: 'Document Intel', path: '/document-intelligence-scanner', icon: <FileText className="h-4 w-4" />, status: 'active', powerLevel: 94, category: 'Document' },
    { id: 'timeline-ball', name: 'Timeline Ball', path: '/uk-timeline-forecaster', icon: <Clock className="h-4 w-4" />, status: 'active', powerLevel: 91, category: 'Analytics' },
    { id: 'risk-assessment', name: 'Risk Assessment', path: '/uk-risk-assessment', icon: <AlertTriangle className="h-4 w-4" />, status: 'active', powerLevel: 86, category: 'Analytics' },
    { id: 'visa-fee-calc', name: 'Visa Fee Calc', path: '/visa-fee-calculator', icon: <Calculator className="h-4 w-4" />, status: 'active', powerLevel: 83, category: 'Financial' },
    { id: 'english-assess', name: 'English Assessor', path: '/uk-english-test', icon: <Languages className="h-4 w-4" />, status: 'active', powerLevel: 84, category: 'Assessment' },
    { id: 'accommodation', name: 'Accommodation', path: '/accommodation-checker', icon: <Home className="h-4 w-4" />, status: 'active', powerLevel: 80, category: 'Planning' },
  ];

  const [blinkingTools, setBlinkingTools] = useState<string[]>([]);
  const [activeConnections, setActiveConnections] = useState<[string, string][]>([]);

  useEffect(() => {
    const blinkInterval = setInterval(() => {
      const randomCount = Math.floor(Math.random() * 5) + 3;
      const shuffled = [...ukTools];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      setBlinkingTools(shuffled.slice(0, randomCount).map(t => t.id));
    }, 800);

    const connectionInterval = setInterval(() => {
      const connectionCount = Math.floor(Math.random() * 6) + 4;
      const newConnections: [string, string][] = [];
      const usedTools = new Set<string>();
      
      for (let i = 0; i < connectionCount && i < ukTools.length - 1; i++) {
        let tool1, tool2;
        do {
          tool1 = ukTools[Math.floor(Math.random() * ukTools.length)].id;
          tool2 = ukTools[Math.floor(Math.random() * ukTools.length)].id;
        } while (tool1 === tool2 || usedTools.has(`${tool1}-${tool2}`) || usedTools.has(`${tool2}-${tool1}`));
        
        newConnections.push([tool1, tool2]);
        usedTools.add(`${tool1}-${tool2}`);
        usedTools.add(`${tool2}-${tool1}`);
      }
      setActiveConnections(newConnections);
    }, 1500);

    return () => {
      clearInterval(blinkInterval);
      clearInterval(connectionInterval);
    };
  }, [ukTools.length]);

  const getConnectionStrength = (tool: any) => {
    if (tool.powerLevel > 90) return 'strong';
    if (tool.powerLevel > 85) return 'medium';
    return 'connected';
  };

  return (
    <div className="relative p-4 bg-white/5 rounded-xl border border-white/10">
      {/* Background Grid */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 to-purple-900/20 rounded-xl">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:20px_20px]"></div>
      </div>

      {/* Connection Lines SVG */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none">
        {activeConnections.map(([tool1Id, tool2Id], idx) => {
          const tool1Index = ukTools.findIndex(t => t.id === tool1Id);
          const tool2Index = ukTools.findIndex(t => t.id === tool2Id);
          const row1 = Math.floor(tool1Index / 4);
          const col1 = tool1Index % 4;
          const row2 = Math.floor(tool2Index / 4);
          const col2 = tool2Index % 4;
          
          const x1 = 12.5 + col1 * 25;
          const y1 = 15 + row1 * 22;
          const x2 = 12.5 + col2 * 25;
          const y2 = 15 + row2 * 22;

          return (
            <line
              key={`connection-${idx}`}
              x1={`${x1}%`}
              y1={`${y1}%`}
              x2={`${x2}%`}
              y2={`${y2}%`}
              stroke="url(#connection-gradient)"
              strokeWidth="1.5"
              strokeDasharray="4,3"
              className="animate-pulse"
            />
          );
        })}
        
        <defs>
          <linearGradient id="connection-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#22c55e" stopOpacity="0.6" />
            <stop offset="50%" stopColor="#3b82f6" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#a855f7" stopOpacity="0.6" />
          </linearGradient>
        </defs>
      </svg>

      {/* Tool Nodes Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 relative z-10">
        {ukTools.map((tool, index) => {
          const isBlinking = blinkingTools.includes(tool.id);
          const hasActiveConnection = activeConnections.some(
            ([t1, t2]) => t1 === tool.id || t2 === tool.id
          );
          const connectionStrength = getConnectionStrength(tool);

          return (
            <Link
              key={tool.id}
              to={tool.path}
              className={`relative p-2 rounded-xl border-2 flex flex-col items-center justify-center cursor-pointer transition-all duration-300 transform hover:scale-105 hover:z-20 ${
                isBlinking 
                  ? 'animate-pulse border-blue-400 bg-blue-500/20' 
                  : hasActiveConnection
                  ? 'border-green-400 bg-green-500/10'
                  : 'border-white/20 bg-white/5 hover:border-blue-500/50'
              }`}
            >
              {/* Blinking Status Light */}
              <div className={`absolute -top-1 -right-1 w-3 h-3 rounded-full ${
                tool.status === 'active' 
                  ? 'bg-green-500 shadow-lg shadow-green-500/50' 
                  : 'bg-red-500'
              } ${isBlinking ? 'animate-ping' : ''}`}>
                <div className={`w-1.5 h-1.5 rounded-full absolute top-0.5 left-0.5 ${
                  tool.status === 'active' ? 'bg-white' : 'bg-gray-300'
                }`} />
              </div>

              {/* Tool Icon */}
              <div className={`mb-1 p-1.5 rounded-lg ${
                connectionStrength === 'strong'
                  ? 'bg-gradient-to-br from-green-500 to-emerald-600'
                  : connectionStrength === 'medium'
                  ? 'bg-gradient-to-br from-yellow-500 to-amber-600'
                  : 'bg-gradient-to-br from-blue-500 to-cyan-600'
              } ${isBlinking ? 'animate-bounce' : ''}`}>
                {tool.icon}
              </div>

              {/* Tool Name */}
              <span className="text-[10px] font-semibold text-white text-center truncate w-full">
                {tool.name}
              </span>

              {/* Power Indicator */}
              <div className="w-full mt-1">
                <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full ${
                      tool.powerLevel > 90 
                        ? 'bg-gradient-to-r from-green-400 to-emerald-500'
                        : tool.powerLevel > 85
                        ? 'bg-gradient-to-r from-yellow-400 to-amber-500'
                        : 'bg-gradient-to-r from-blue-400 to-cyan-500'
                    }`}
                    style={{ width: `${tool.powerLevel}%` }}
                  />
                </div>
              </div>

              {/* Active Connection Indicator */}
              {hasActiveConnection && (
                <div className="absolute -bottom-1 w-2 h-2 rounded-full bg-green-400 animate-pulse">
                  <div className="absolute inset-0 rounded-full bg-green-400 animate-ping"></div>
                </div>
              )}
            </Link>
          );
        })}
      </div>

      {/* Matrix Status Footer */}
      <div className="mt-4 pt-3 border-t border-white/10 relative z-10">
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
              <span className="text-white/60">Active & Blinking</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-blue-500"></div>
              <span className="text-white/60">Connected</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse"></div>
              <span className="text-white/60">Data Transfer</span>
            </div>
          </div>
          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[8px] font-medium bg-gradient-to-r from-blue-500 to-purple-600 text-white">
            <Radio className="h-2 w-2 mr-0.5" />
            {blinkingTools.length} Tools Active
          </span>
        </div>
      </div>
    </div>
  );
};

// ============================
// BUTTON COMPONENT
// ============================
const Button = ({ children, variant = 'primary', className = '', ...props }: any) => {
  const base = 'inline-flex items-center justify-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 rounded-xl font-bold transition-all duration-200 text-sm sm:text-base';
  const variants = {
    primary: 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:shadow-xl hover:scale-105',
    secondary: 'border border-white/20 bg-white/5 text-white hover:bg-white/10 hover:scale-105',
    outline: 'border-2 border-blue-500 text-blue-400 hover:bg-blue-500/10 hover:scale-105'
  };
  return (
    <button className={`${base} ${variants[variant]} ${className}`} {...props}>
      {children}
    </button>
  );
};

// ============================
// SECTION HEADER
// ============================
const SectionHeader = ({ eyebrow, title, description, align = 'center' }: any) => (
  <div className={`space-y-3 max-w-3xl mx-auto ${align === 'left' ? 'text-left mx-0' : 'text-center'}`}>
    {eyebrow && <span className="text-sm font-semibold text-blue-400 uppercase tracking-wider">{eyebrow}</span>}
    <h2 className="text-3xl md:text-4xl font-black text-white tracking-tight">{title}</h2>
    {description && <p className="text-white/70 text-lg">{description}</p>}
  </div>
);

// ============================
// TOOL CARD
// ============================
const ToolCard = ({ icon: Icon, name, description, path, tags }: any) => (
  <Link to={path} className="block group">
    <div className="bg-white/5 backdrop-blur-sm rounded-xl p-5 border border-white/10 hover:border-blue-500/50 transition-all hover:-translate-y-1 h-full">
      <div className="flex items-start justify-between mb-3">
        <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500">
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="flex gap-1">
          {tags.map((tag: string, i: number) => (
            <span key={i} className="text-[10px] bg-white/10 px-2 py-0.5 rounded-full text-white/70">{tag}</span>
          ))}
        </div>
      </div>
      <h3 className="font-bold text-white mb-1 group-hover:text-blue-300 transition-colors">{name}</h3>
      <p className="text-sm text-white/60 line-clamp-2">{description}</p>
      <div className="mt-3 flex items-center text-blue-400 text-sm font-semibold group-hover:gap-2 transition-all gap-1">
        Assess now <ArrowRight className="w-4 h-4" />
      </div>
    </div>
  </Link>
);

// ============================
// VISA PATHWAY CARD
// ============================
const VisaPathwayCard = ({ title, icon: Icon, description, path }: any) => (
  <Link to={path} className="block group">
    <div className="bg-white/5 backdrop-blur-sm rounded-xl p-5 border border-white/10 hover:border-blue-500/50 transition-all">
      <div className="flex items-center gap-3 mb-3">
        <Icon className="w-6 h-6 text-blue-400" />
        <h3 className="font-bold text-white text-lg">{title}</h3>
      </div>
      <p className="text-white/60 text-sm mb-3">{description}</p>
      <div className="text-blue-400 text-sm font-semibold group-hover:underline">Learn more →</div>
    </div>
  </Link>
);

// ============================
// ORIGINAL HERO BANNER
// ============================
const Hero = () => (
  <section className="relative overflow-hidden pt-20 pb-16 md:pt-28 md:pb-24">
    <div className="container mx-auto px-4">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <div className="space-y-6 text-center lg:text-left">
          <div className="inline-flex items-center gap-2 bg-red-500/20 text-red-300 px-4 py-2 rounded-full text-sm font-semibold backdrop-blur-sm">
            <Flag className="w-4 h-4" /> United Kingdom
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white leading-tight">
            Your complete{' '}
            <span className="bg-gradient-to-r from-red-400 to-blue-400 bg-clip-text text-transparent">
              UK immigration
            </span>{' '}
            co‑pilot
          </h1>
          <p className="text-lg text-white/70 max-w-xl mx-auto lg:mx-0">
            From student to skilled worker, global talent to family visa – VisaGuide OS orchestrates every step.
          </p>
          <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
            <Link to="/signup">
              <Button>Start UK Assessment <Flag className="w-4 h-4" /></Button>
            </Link>
            <Link to="/signup">
              <Button variant="secondary">Explore All UK Tools <Zap className="w-4 h-4" /></Button>
            </Link>
          </div>
          <div className="flex items-center gap-6 justify-center lg:justify-start text-sm text-white/50">
            <span className="flex items-center gap-1"><CheckCircle2 className="w-4 h-4 text-green-400" /> 66+ Tools</span>
            <span className="flex items-center gap-1"><Users className="w-4 h-4 text-blue-400" /> 2.3k+ Success Stories</span>
            <span className="flex items-center gap-1"><Brain className="w-4 h-4 text-purple-400" /> AI-Powered</span>
          </div>
        </div>
        <div className="relative">
          {/* Personalized Intelligence Profile - Replacing placeholder */}
          <PersonalizedIntelligenceProfile />
        </div>
      </div>
    </div>
  </section>
);

// ============================
// KEY TOOLS SECTION WITH CONNECTION MATRIX
// ============================
const KeyTools = () => {
  const tools = [
    { icon: Briefcase, name: 'Skilled Worker Assessment', description: 'Points-based system check – job offer, salary, English.', path: '/skilled-worker-assessment', tags: ['Assessment'] },
    { icon: GraduationCap, name: 'Student Visa Assessment', description: 'CAS readiness, financial evidence, English level.', path: '/student-assessment', tags: ['Assessment'] },
    { icon: Heart, name: 'Family Sponsorship Assessment', description: 'Spouse, partner, parent routes.', path: '/family-assessment', tags: ['Assessment'] },
    { icon: Stethoscope, name: 'Health & Care Worker Assessment', description: 'GMC/NMC/GPhC requirements.', path: '/health-care-assessment', tags: ['Assessment'] },
    { icon: Users, name: 'Youth Mobility Assessment', description: 'Working holiday eligibility.', path: '/youth-mobility-assessment', tags: ['Assessment'] },
    { icon: Award, name: 'Global Talent Assessment', description: 'Endorsement body matching.', path: '/global-talent-assessment', tags: ['Assessment'] },
    { icon: AlertTriangle, name: 'Risk Assessment Engine', description: 'AI-powered risk analysis.', path: '/uk-risk-assessment', tags: ['AI'] },
    { icon: TrendingUp, name: 'Success Prediction Engine', description: 'Visa approval probability.', path: '/uk-success-prediction', tags: ['AI'] },
    { icon: Calendar, name: 'Timeline Crystal Ball', description: 'Processing time predictions.', path: '/uk-timeline-forecaster', tags: ['AI'] },
    { icon: FileText, name: 'Document Intelligence', description: 'AI review, gap detection.', path: '/document-intelligence-scanner', tags: ['Document'] },
    { icon: DollarSign, name: 'Global Salary Calculator', description: 'Compare salaries across countries.', path: '/uk-salary-calculator', tags: ['Financial'] },
    { icon: Calculator, name: 'Visa Fee Calculator', description: 'Fee breakdown with IHS.', path: '/visa-fee-calculator', tags: ['Financial'] },
    { icon: Languages, name: 'English Language Assessor', description: 'IELTS/PTE/Trinity mapping.', path: '/uk-english-test', tags: ['Calculator'] },
    { icon: Home, name: 'Accommodation Checker', description: 'Housing standards, overcrowding.', path: '/accommodation-checker', tags: ['Planning'] }
  ];
  
  return (
    <section className="py-20 relative z-10">
      <div className="container mx-auto px-4 max-w-6xl">
        <SectionHeader
          eyebrow="UK Tools"
          title="Everything you need for a successful UK visa"
          description="From assessments to document intelligence, timeline prediction to financial calculators with a unified system sharing information in real time with AI orchestrator."
        />
        
        {/* Tool Connection Matrix with Blinking Lights */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-yellow-400 animate-pulse" />
              <h3 className="text-lg font-semibold text-white">Tool Connection Matrix</h3>
              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-gradient-to-r from-green-500 to-emerald-600 text-white animate-pulse">
                <Radio className="h-2.5 w-2.5 mr-1" />
                LIVE CONNECTIONS
              </span>
            </div>
            <p className="text-white/60 text-sm hidden md:block">All premium tools are connected and communicating in real-time</p>
          </div>
          <UKToolsConnectionMatrix />
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {tools.map((tool, i) => (
            <ToolCard key={i} {...tool} />
          ))}
        </div>
        <div className="text-center mt-10">
          <Link to="/signup">
            <Button variant="secondary">View all 66+ UK tools <ArrowRight className="w-4 h-4" /></Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

// ============================
// VISA PATHWAYS
// ============================
const VisaPathways = () => {
  const pathways = [
    { title: 'Skilled Worker', icon: Briefcase, description: 'Points-based work visa.', path: '/skilled-worker-assessment' },
    { title: 'Student Visa', icon: GraduationCap, description: 'Study in the UK.', path: '/student-assessment' },
    { title: 'Global Talent', icon: Award, description: 'For leaders in tech, arts, academia.', path: '/global-talent-assessment' },
    { title: 'Innovator Founder', icon: Rocket, description: 'For innovative business ideas.', path: '/business-assessment' },
    { title: 'Family Visa', icon: Heart, description: 'Sponsor spouse, partner, children.', path: '/family-assessment' },
    { title: 'Health & Care Worker', icon: Stethoscope, description: 'For medical professionals.', path: '/health-care-assessment' }
  ];
  
  return (
    <section className="py-20 bg-white/5 rounded-3xl my-8 relative z-10">
      <div className="container mx-auto px-4 max-w-6xl">
        <SectionHeader
          eyebrow="Visa Pathways"
          title="Choose your route"
          description="VisaGuide OS supports all major UK visa categories."
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
          {pathways.map((pathway, i) => (
            <VisaPathwayCard key={i} {...pathway} />
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================
// BONUS FEATURES
// ============================
const BonusFeatures = () => (
  <section className="py-20 relative z-10">
    <div className="container mx-auto px-4 max-w-6xl">
      <SectionHeader
        eyebrow="Bonus Intelligence"
        title="Go beyond the visa"
        description="Unlock exclusive tools to save money and find opportunities."
      />
      <div className="grid md:grid-cols-2 gap-8 mt-12">
        <div className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-2xl p-6 border border-cyan-500/30">
          <div className="flex items-center gap-3 mb-4">
            <Plane className="w-8 h-8 text-cyan-400" />
            <h3 className="text-2xl font-bold text-white">Flight Intelligence</h3>
          </div>
          <p className="text-white/70 mb-4">Find the cheapest flights for your move – real‑time price prediction and booking assistant.</p>
          <div className="bg-white/5 rounded-xl p-4 mb-4">
            <div className="flex justify-between text-white/80 text-sm">
              <span>London → Toronto</span>
              <span className="text-green-400">£289</span>
            </div>
            <div className="flex justify-between text-white/80 text-sm mt-2">
              <span>Manchester → Vancouver</span>
              <span className="text-green-400">£342</span>
            </div>
            <div className="text-center text-xs text-white/40 mt-2">Price trend: ▼12% next week</div>
          </div>
          <Link to="/signup">
            <Button variant="secondary" className="w-full">Search flights <Plane className="w-4 h-4" /></Button>
          </Link>
        </div>
        <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-2xl p-6 border border-purple-500/30">
          <div className="flex items-center gap-3 mb-4">
            <Briefcase className="w-8 h-8 text-purple-400" />
            <h3 className="text-2xl font-bold text-white">Opportunity Hub</h3>
          </div>
          <p className="text-white/70 mb-4">Discover jobs, scholarships, and training programmes tailored to your visa pathway.</p>
          <div className="space-y-2 mb-4">
            <div className="flex items-center gap-2 text-white/80"><CheckCircle2 className="w-4 h-4 text-green-400" /> 1,200+ sponsored jobs</div>
            <div className="flex items-center gap-2 text-white/80"><CheckCircle2 className="w-4 h-4 text-green-400" /> 350+ scholarships</div>
            <div className="flex items-center gap-2 text-white/80"><CheckCircle2 className="w-4 h-4 text-green-400" /> Language courses</div>
          </div>
          <Link to="/signup">
            <Button variant="secondary" className="w-full">Explore opportunities <ArrowRight className="w-4 h-4" /></Button>
          </Link>
        </div>
      </div>
    </div>
  </section>
);

// ============================
// AI-POWERED SIMULATION SUITE (FIXED)
// ============================
const AIPoweredSimulationSuite = () => {
  const simulations = [
    {
      title: 'UK Visa Simulation',
      description: 'Quantum-powered journey mapping with real-time processing intelligence. Complete your entire application in a simulated environment.',
      icon: Command,
      path: '/uk-visa-simulator/dashboard',
      badge: 'Quantum Powered',
      gradient: 'from-blue-900/50 to-cyan-900/50'
    },
    {
      title: 'AI Interview Simulator',
      description: 'AI-driven practice with region‑specific questions, real‑time feedback, and confidence scoring. Prepare for your credibility interview.',
      icon: Mic,
      path: '/interview-simulator',
      badge: 'AI Coach',
      gradient: 'from-purple-900/50 to-pink-900/50'
    },
    {
      title: 'AI Form Assistant',
      description: 'Intelligent auto‑fill and step‑by‑step guidance for UKVI forms. Reduce errors and save hours of manual data entry.',
      icon: FileCode,
      path: '/visa-form-os',
      badge: 'Smart Form',
      gradient: 'from-emerald-900/50 to-teal-900/50'
    }
  ];

  return (
    <section className="py-20 relative z-10">
      <div className="container mx-auto px-4 max-w-6xl">
        <SectionHeader
          eyebrow="AI‑Powered Simulation Suite"
          title="Practice. Perfect. Succeed."
          description="Experience your visa journey before you submit – from end‑to‑end simulation to interview coaching."
        />
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {simulations.map((sim, i) => {
            const IconComponent = sim.icon;
            return (
              <Link key={i} to={sim.path} className="block group">
                <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden hover:border-blue-500/50 transition-all hover:-translate-y-1 h-full">
                  <div className={`aspect-video bg-gradient-to-br ${sim.gradient} flex items-center justify-center p-4 border-b border-white/10 relative overflow-hidden`}>
                    <div className="absolute inset-0 opacity-20">
                      <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.1),transparent_50%)]" />
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-16 h-16 rounded-full border border-blue-500/30 animate-ping absolute"></div>
                      <div className="w-12 h-12 rounded-full border border-purple-500/40 animate-spin-slow absolute"></div>
                      <div className="w-8 h-8 rounded-full border border-cyan-500/50 animate-pulse absolute"></div>
                    </div>
                    <div className="relative z-10 p-3 rounded-2xl bg-gradient-to-br from-blue-600/40 to-purple-600/40 backdrop-blur-sm border border-white/20 shadow-xl animate-float">
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    <div className="absolute bottom-2 right-2 flex gap-1">
                      <div className="w-1 h-1 rounded-full bg-blue-400 animate-pulse" />
                      <div className="w-1 h-1 rounded-full bg-purple-400 animate-pulse delay-150" />
                      <div className="w-1 h-1 rounded-full bg-pink-400 animate-pulse delay-300" />
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[10px] bg-gradient-to-r from-blue-500/20 to-purple-500/20 px-2 py-0.5 rounded-full text-blue-300 border border-blue-500/30">{sim.badge}</span>
                    </div>
                    <h3 className="text-lg font-bold text-white mb-1 group-hover:text-blue-300 transition-colors">{sim.title}</h3>
                    <p className="text-white/60 text-xs mb-3 line-clamp-2">{sim.description}</p>
                    <div className="flex items-center text-blue-400 text-xs font-semibold group-hover:gap-2 transition-all gap-1">
                      Launch Simulator <ArrowRight className="w-3 h-3" />
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ============================
// TESTIMONIALS
// ============================
const Testimonials = () => (
  <section className="py-20 relative z-10">
    <div className="container mx-auto px-4 max-w-6xl">
      <SectionHeader
        eyebrow="Success Stories"
        title="Trusted by thousands"
        description="Real people, real visas, real results."
      />
      <div className="grid md:grid-cols-3 gap-8 mt-12">
        {[
          { name: 'Sarah Chen', role: 'Student Visa', quote: 'The AI interview simulator helped me feel completely prepared. I passed my credibility interview with confidence!', rating: 5 },
          { name: 'Michael Okafor', role: 'Skilled Worker', quote: 'Document Intelligence caught a missing bank statement that would have delayed my application by weeks. Saved me!', rating: 5 },
          { name: 'Priya Sharma', role: 'Global Talent', quote: 'The Timeline Crystal Ball predicted my decision within 2 days of the actual date. Unbelievable accuracy.', rating: 5 }
        ].map((testimonial, i) => (
          <div key={i} className="bg-white/10 rounded-2xl p-6">
            <div className="flex gap-1 mb-4">
              {[...Array(testimonial.rating)].map((_, s) => (
                <StarIcon key={s} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <p className="text-white/80 mb-4">"{testimonial.quote}"</p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold">
                {testimonial.name.charAt(0)}
              </div>
              <div>
                <div className="text-white font-semibold">{testimonial.name}</div>
                <div className="text-white/50 text-sm">{testimonial.role}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ============================
// OS EXPLORER PROMO (NEW CONTAINER)
// ============================
const OSExplorerPromo = () => (
  <section className="py-16 relative z-10">
    <div className="container mx-auto px-4 max-w-6xl">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-600/10 via-purple-600/10 to-red-600/10 border border-white/20 backdrop-blur-xl p-8 md:p-12">
        {/* Animated ring elements */}
        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full border border-blue-500/20 animate-spin-slow" />
        <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full border border-purple-500/20 animate-spin-slow" style={{ animationDirection: 'reverse' }} />
        
        <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="text-center md:text-left flex-1">
            <div className="inline-flex items-center gap-2 bg-blue-500/20 text-blue-300 px-4 py-1.5 rounded-full text-sm font-semibold mb-4 backdrop-blur-sm">
              <Sparkles className="w-4 h-4" /> NEW INTERACTIVE DEMO
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
              Experience the{' '}
              <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-red-400 bg-clip-text text-transparent">
                UK Immigration OS
              </span>{' '}
              in action
            </h2>
            <p className="text-white/70 text-lg max-w-2xl">
              See how VisaGuide OS orchestrates assessments, documents, predictions, and agency tools in one unified platform — no sign‑up required.
            </p>
            <div className="flex flex-wrap gap-4 mt-6 justify-center md:justify-start">
              <Link to="/signup">
                <Button variant="premium" className="gap-3">
                  Launch OS Explorer <Rocket className="w-5 h-5" />
                </Button>
              </Link>
              <Link to="/signup">
                <Button variant="secondary">
                  Explore All Tools <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
          <div className="hidden md:block">
            <div className="w-32 h-32 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-2xl animate-float">
              <Eye className="w-12 h-12 text-white" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ============================
// CALL TO ACTION
// ============================
const CTA = () => (
  <section className="py-20 bg-gradient-to-r from-red-600/20 to-blue-600/20 rounded-3xl my-10 relative z-10">
    <div className="container mx-auto px-4 text-center">
      <h2 className="text-3xl md:text-4xl font-black text-white mb-4">Ready to start your UK journey?</h2>
      <p className="text-white/70 text-lg mb-8 max-w-2xl mx-auto">
        Join thousands who have successfully navigated the UK immigration system with VisaGuide OS.
      </p>
      <div className="flex flex-wrap gap-4 justify-center">
        <Link to="/signup"><Button>Start UK Assessment <Flag className="w-4 h-4" /></Button></Link>
        <Link to="/pricing"><Button variant="secondary">View Pricing <Crown className="w-4 h-4" /></Button></Link>
      </div>
    </div>
  </section>
);

// ============================
// FOOTER (Homepage style)
// ============================
const Footer = () => {
  const flagEmojis = ['🇬🇧', '🇨🇦', '🇦🇺', '🇩🇪', '🇫🇷', '🇳🇿'];
  // `path: undefined` means there's no real page for it yet (API Docs,
  // Roadmap, Careers, Press, Terms, Security, a standalone France hub) --
  // these previously linked to `href="#"`, a dead link that looked
  // clickable but did nothing. Rendered as plain text below instead of a
  // link to nowhere; wire them up once those pages exist.
  const columns: { title: string; links: { name: string; path?: string }[] }[] = [
    { title: 'Product', links: [
      { name: 'Features', path: '/features' },
      { name: 'Pricing', path: '/pricing' },
      { name: 'API Docs' },
      { name: 'Roadmap' },
    ] },
    { title: 'Countries', links: [
      { name: 'United Kingdom', path: '/uk-immigration-hub' },
      { name: 'Canada', path: '/canada-immigration' },
      { name: 'Australia', path: '/australia-immigration' },
      { name: 'Germany', path: '/germany-immigration' },
      { name: 'France' },
      { name: 'New Zealand', path: '/new-zealand-immigration' },
    ] },
    { title: 'Company', links: [
      { name: 'About', path: '/about' },
      { name: 'Blog', path: '/blog' },
      { name: 'Careers' },
      { name: 'Press' },
    ] },
    { title: 'Legal', links: [
      { name: 'Privacy Policy', path: '/privacy' },
      { name: 'Terms' },
      { name: 'Security' },
      { name: 'Accessibility', path: '/accessibility' },
    ] },
  ];

  return (
    <footer className="relative z-10 border-t border-white/10 pt-12 pb-8 mt-12">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 mb-10">
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 flex items-center justify-center shadow-lg">
                <Rocket className="w-4 h-4 text-white" />
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">VisaGuide OS</span>
            </div>
            <p className="text-white/50 text-sm leading-relaxed mb-4">
              The world's first AI-native Global Mobility Operating System. Covering 6 countries. 250+ modules.
            </p>
            <div className="flex gap-2">
              {flagEmojis.map((flag, idx) => (
                <span key={idx} className="text-2xl">{flag}</span>
              ))}
            </div>
          </div>

          {columns.map((col) => (
            <div key={col.title}>
              <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">{col.title}</h4>
              <ul className="space-y-2">
                {col.links.map((link) => (
                  <li key={link.name}>
                    {link.path ? (
                      <Link to={link.path} className="text-white/40 text-sm hover:text-white transition-colors duration-200">
                        {link.name}
                      </Link>
                    ) : (
                      <span className="text-white/20 text-sm cursor-default">{link.name}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="pt-6 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-white/30 text-xs">
            © {new Date().getFullYear()} VisaGuide OS. All rights reserved. Privacy-by-Design • Explainable AI • Audit-Ready
          </p>
          <div className="flex gap-3">
            <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-medium bg-blue-500/10 text-blue-300 border border-blue-500/20">AI-Native</span>
            <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-medium bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">GDPR Compliant</span>
            <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-medium bg-purple-500/10 text-purple-300 border border-purple-500/20">SOC 2</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

// ============================
// MAIN EXPORT
// ============================
export default function UKImmigrationHub() {
  return (
    <div className="min-h-screen relative overflow-x-hidden">
      <AnimatedBackground />
      <Hero />
      <KeyTools />
      <VisaPathways />
      <BonusFeatures />
      <AIPoweredSimulationSuite />
      <Testimonials />
      <OSExplorerPromo />
      <CTA />
      <Footer />
    </div>
  );
}